import { useEffect } from "react";
import { messaging, getToken, onMessage, VAPID_KEY, isConfigured } from "config/firebase";
import { notificationService } from "services/notificationService";
import { useNotification } from "context/NotificationContext";
import { useLanguage } from "context/LanguageContext";

const SW_FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Module-level so concurrent mounts (StrictMode, fast navigation) don't each
// kick off their own registration. Cleared on logout so re-login works.
let registrationPromise: Promise<void> | null = null;

// Called explicitly from sign-in flows because App never re-renders after
// navigate() in a child component, so the useEffect([isLoggedIn]) guard
// inside useFCM would never fire on the first login.
export function triggerFCMRegistration(): void {
  if (!isConfigured || !messaging) return;
  if (registrationPromise) return;

  registrationPromise = registerFCMDevice().catch((err) => {
    registrationPromise = null;
    console.warn("[FCM] Device registration failed:", err);
  });
}

async function registerFCMDevice(): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return;

  await navigator.serviceWorker.register("/firebase-messaging-sw.js");

  // navigator.serviceWorker.ready waits for a fully active SW — avoids the
  // "Could not establish connection" race when the SW process is terminated.
  const registration = await navigator.serviceWorker.ready;

  if (!registration.active) return;

  // Handshake: wait for the SW to acknowledge the config before calling
  // getToken so Firebase's internal channel is ready.
  await new Promise<void>((resolve) => {
    const timeout = setTimeout(resolve, 3000);
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
      clearTimeout(timeout);
      resolve();
    };
    registration.active!.postMessage(
      { type: "FIREBASE_CONFIG", config: SW_FIREBASE_CONFIG },
      [channel.port2]
    );
  });

  const token = await getToken(messaging!, {
    vapidKey: VAPID_KEY || undefined,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    console.warn("[FCM] No token returned — check VAPID key and service worker");
    return;
  }

  // Skip the backend call when this token was already registered in this session.
  if (sessionStorage.getItem("fcm_token") === token) return;

  await notificationService.registerDevice({
    fcm_token: token,
    platform: "WEB",
    device_name: navigator.userAgent.slice(0, 100),
  });

  sessionStorage.setItem("fcm_token", token);
}

export function useFCM(onForegroundMessage?: () => void) {
  const { addNotification } = useNotification();
  const { t } = useLanguage();
  const isLoggedIn = !!sessionStorage.getItem("access_token");

  // Clear the module-level guard when the user logs out so that a subsequent
  // login triggers a fresh device registration instead of being skipped.
  useEffect(() => {
    if (!isLoggedIn) {
      registrationPromise = null;
    }
  }, [isLoggedIn]);

  // Only register after login — registerDevice needs a valid access_token.
  // Re-runs whenever isLoggedIn flips (login / logout / token expiry redirect).
  useEffect(() => {
    if (!isLoggedIn || !isConfigured || !messaging) return;
    if (registrationPromise) return;

    registrationPromise = registerFCMDevice().catch((err) => {
      registrationPromise = null; // allow retry on next render after transient failure
      console.warn("[FCM] Device registration failed:", err);
    });
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isConfigured || !messaging) return;

    return onMessage(messaging, (payload) => {
      const title = payload.notification?.title ?? t("notify.newForeground");
      const body = payload.notification?.body ?? "";
      addNotification(body ? `${title}: ${body}` : title, "info", 5000);
      onForegroundMessage?.();
    });
  }, [addNotification, onForegroundMessage, t]);
}
